import { useState, useEffect } from "react";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";

import { signOut } from "next-auth/react";

import { Input } from "@/components/ui/input";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";

import { Button } from "@/components/ui/button";

import { Edit, CheckCircle, Settings2, Trash2 } from "lucide-react";
import { isAdmin, isMember } from "@/lib/api/client-authentication";

import { useToast } from "@/components/ui/use-toast";
import { useRouter } from "next/navigation";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
  } from "@/components/ui/alert-dialog"
  

type AccountProps = {
	lastName: string;
	firstName: string;
	userId: string;
	onUpdateName: any;
};
export default function Account(props: AccountProps) {
	const { toast } = useToast();

	const [lastName, setLastName] = useState(props?.lastName);
	const [firstName, setFirstName] = useState(props?.firstName);

	const [editable, setEditable] = useState(false);
	const [showForm, setShowForm] = useState(false);
	const [showSettings, setShowSettings] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		const checkIfUserIsAdmin = async () => {
			if ((await isMember(props.userId)) || (await isAdmin())) {
				setEditable(true);
			} else {
				
				setEditable(false);
			}
		};

		checkIfUserIsAdmin();
	}, []);

	const accountFormSchema = z.object({
		firstName: z
			.string()
			.trim()
			.min(1, "First name cannot only contain spaces"),
		lastName: z
			.string()
			.trim()
			.min(1, "Last name cannot only contain spaces"),
	});

	type AccountFormValues = z.infer<typeof accountFormSchema>;

	const defaultValues: Partial<AccountFormValues> = {
		firstName: firstName,
		lastName: lastName,
	};

	const form = useForm<AccountFormValues>({
		resolver: zodResolver(accountFormSchema),
		defaultValues,
		mode: "onChange",
	});

	const onSubmit = async (data: AccountFormValues) => {
		const toastId = toast({
			title: "Saving...",
		});

		try {
			const res = await fetch("/api/user", {
				method: "PATCH",
				body: JSON.stringify({
					userId: props.userId,
					firstName: data.firstName.trim(),
					lastName: data.lastName.trim(),
				}),
			});

			toastId.update(
				toast({
					variant: "default",
					duration: 1300,
					action: (
						<div className="w-full flex items-center !ml-0">
							<CheckCircle color="#22c55e" className="mr-2" />
							<span className="text-sm font-semibold">Saved</span>
						</div>
					),
				})
			);

			props.onUpdateName(data.firstName.trim(), data.lastName.trim());
			setFirstName(data.firstName.trim());
			setLastName(data.lastName.trim());
		} catch (e: any) {
			console.log("The error is", e);
			toastId.update(
				toast({
					title: "Something went wrong",
					duration: 1300,
					description: e.message,
					variant: "destructive",
				})
			);
		}

		setShowForm(false);
	};


	const handleConfirmDelete = async () => {
		setIsDeleting(true);
		let result = await fetch(`/api/user?id=${props.userId}`, {
			method: "DELETE",
		});
		let res = await result.json();
		setIsDeleting(false);
		console.log({ res });
		await signOut({ callbackUrl: "/" });
	};

	
	return (
		<div className="flex flex-col items-center justify-center break-all w-full">
			{editable && (
				<>

					<Dialog open={showSettings} onOpenChange={setShowSettings}>
						<DialogTrigger asChild>
							<Button className="self-start hover:bg-blue-500">
								<Settings2 color="#ffffff" className="mr-2" />
								Account Settings
							</Button>
						</DialogTrigger>

						<DialogContent className="dialogScrollbar h-[625px] overflow-y-auto sm:max-w-[425px] ">
							<DialogHeader>
								<DialogTitle className="pt-3">Account Settings</DialogTitle>
								<DialogDescription>
									Change Account Settings
								</DialogDescription>
							</DialogHeader>

							{/* Edit */}
							<div>
								{" "}
								<hr className="h-4 " />

								<div className="flex items-center gap-2 mb-6">
									<h2 className="text-2xl font-bold">Edit Account</h2>
									<Edit />
								</div>
								<p className="text-gray-700 mb-6">Edit and update your profile name.</p>


								<Button className="hover:bg-blue-500"
									onClick={() => {
										setShowForm((prev) => !prev)
									}}
								>
									<Edit color="#ffffff" className="mr-2" />
									Edit Account
								</Button>

								{
									showForm && (
								
										<Form {...form}>
											<form
												onSubmit={form.handleSubmit(onSubmit)}
												className="space-y-8 m-6 " >
												<FormField
													control={form.control}
													name="firstName"
													render={({ field }) => (
														<FormItem>
															<FormLabel>First Name</FormLabel>
															<FormControl>
																<Input required {...field} />
															</FormControl>

															<FormMessage />
														</FormItem>
													)}
												/>

												<FormField
													control={form.control}
													name="lastName"
													render={({ field }) => (
														<FormItem>
															<FormLabel>Last Name</FormLabel>
															<FormControl>
																<Input required {...field} />
															</FormControl>

															<FormMessage />
														</FormItem>
													)}
												/>

												<Button type="submit" className="hover:bg-blue-500">
													Update Account
												</Button>
											</form>
										</Form>
									)

								}


							</div>

							{/* Delete */}
							<div>
								{" "}
								<hr className="h-4 "/>
								<div className="flex items-center gap-2 mb-6">
									<h2 className="text-2xl text-red-500 font-bold ">Delete Account </h2>
									<Trash2 className="text-red-500"/>
								</div>
								<p className="text-gray-700 mb-6">
									Permanently delete your account and all associated data.This action cannot be undone.
								</p>
								

								<AlertDialog>

									<AlertDialogTrigger
									
										disabled={isDeleting}
										className={`w-full bg-red-500 text-white px-4 py-3 rounded font-medium  ${isDeleting ? "disabled:opacity-40 cursor-not-allowed" : "hover:bg-red-700"}`} >
										
										{isDeleting ? "Deleting..." : "Delete Account"}
									</AlertDialogTrigger>

									

									<AlertDialogContent>
										<AlertDialogHeader>
											<AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
											<AlertDialogDescription>
												This action cannot be undone. This will permanently delete your account
												and remove your data from our servers.
											</AlertDialogDescription>
										</AlertDialogHeader>

										<AlertDialogFooter>
											<AlertDialogCancel
											className="text-gray-700 hover:text-gray-900 px-4 py-3 rounded">
												Cancel
											</AlertDialogCancel>

											<AlertDialogAction 
												onClick={handleConfirmDelete}
												className="text-center bg-red-500 text-white px-4 py-3 rounded font-medium hover:bg-red-700">

													Confirm Deletion

											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>

								</AlertDialog>

							</div>

						</DialogContent>
					</Dialog>
					
				</>
			)}

			<div className="text-center">
				<p className="mt-7 text-zinc-400 text-md">First Name</p>
				<h3 className="font-semibold text-4xl mb-2 sm:text-5xl">
					{firstName}
				</h3>

				<p className="mt-5 text-zinc-400 text-md">Last Name</p>
				<h3 className="font-semibold text-4xl sm:text-5xl">{lastName}</h3>
			</div>
		</div>
	);
}
